/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

@file:Suppress("unused")

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.secretbox.SecretBoxCorruptedOrTamperedDataExceptionOrInvalidKey
import com.ionspin.kotlin.crypto.util.LibsodiumRandom
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.channels.ReceiveChannel
import net.sergeych.bintools.DataSource


suspend fun readVarUnsigned(input: ReceiveChannel<UByte>): UInt {
    var result = 0u
    var cnt = 0
    while(true) {
        val b = input.receive().toUInt()
        result = (result shl 7) or (b and 0x7fu)
        if( (b and 0x80u) != 0u ) {
            return result
        }
        if( ++cnt > 5 ) throw IllegalArgumentException("overflow while decoding varuint")
    }
}

fun encodeVarUnsigned(value: UInt): UByteArray {
    val result = mutableListOf<UByte>()
    var rest = value
    do {
        val mask = if( rest <= 0x7fu ) 0x80u else 0u
        result.add( (mask or (rest and 0x7fu)).toUByte() )
        rest = rest shr 7
    } while(rest != 0u)
    return result.toUByteArray()
}


fun randomUBytes(n: Int): UByteArray = if (n > 0) LibsodiumRandom.buf(n) else ubyteArrayOf()

fun randomUBytes(n: UInt): UByteArray = if (n > 0u) LibsodiumRandom.buf(n.toInt()) else ubyteArrayOf()

/**
 * Uniform random in `0 ..< max` range
 */
fun randomUInt(max: UInt) = LibsodiumRandom.uniform(max)
fun randomUInt(max: Int) = LibsodiumRandom.uniform(max.toUInt())

fun randomInt(range: IntRange): Int {
    val l = range.last - range.first + 1
    return randomUInt(l.toUInt()).toInt() + range.first
}

fun randomInt() = randomInt( Int.MIN_VALUE ..< Int.MAX_VALUE)

fun <T: Comparable<T>>T.limit(range: ClosedRange<T>) = when {
    this < range.start -> range.start
    this > range.endInclusive -> range.endInclusive
    else -> this
}

fun <T: Comparable<T>>T.limitMax(max: T) = if( this < max ) this else max
fun <T: Comparable<T>>T.limitMin(min: T) = if( this > min ) this else min

/**
 * Properly catch various exceptions, and rethrow them as [DecryptionFailedException], but rethrow
 * [CancellationException] and [DecryptionFailedException] if thrown.
 */
fun <T> protectDecryption(f: () -> T): T {
    return try {
        f()
    } catch (x: Exception) {
        when (x) {
            is SecretBoxCorruptedOrTamperedDataExceptionOrInvalidKey,
            is DataSource.EndOfData,
            -> throw DecryptionFailedException(cause = x)

            is CancellationException, is DecryptionFailedException -> throw x
            else -> {
                println("unexpected exception while decrypting:\n${x.stackTraceToString()}")
                throw DecryptionFailedException(cause = x)
            }
        }
    }
}

